import type {
    IAIAssistantComponentProperties,
    ICheckboxComponentProperties,
    ICVUploadComponentProperties,
    IEmployeeTalkTemplateComponent,
    IMultilineInputComponentProperties,
    INameGenderComponentProperties,
    IOnboardingProgressComponentProperties,
    ISelectComponentProperties,
    ISkillExtractionComponentProperties,
    ISuggestionComponentProperties,
    ITextComponentProperties,
    ITextInputComponentProperties,
    IWelcomeComponentProperties,
    TEmployeeTalkComponentProperties,
    TEmployeeTalkComponentType,
} from '@sharedInterfaces/IEmployeeTalkTemplate';
import { type ELanguage, getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import React from 'react';

import type { ITemplateComponentProps } from '../TemplateEditor/types';
import ETTAIAssistantComponent from './ETTAIAssistantComponent';
import ETTCertificateSelectionComponent from './ETTCertificateSelectionComponent';
import ETTCheckboxComponent from './ETTCheckboxComponent';
import ETTCompletionComponent from './ETTCompletionComponent';
import ETTMultilineInputComponent from './ETTMultilineInputComponent';
import ETTNameGenderComponent from './ETTNameGenderComponent';
import ETTOnboardingCVUploadComponent from './ETTOnboardingCVUploadComponent';
import ETTOnboardingProgressComponent from './ETTOnboardingProgressComponent';
import ETTResultsOverviewComponent from './ETTResultsOverviewComponent';
import ETTRoleSelectionComponent from './ETTRoleSelectionComponent';
import ETTSelectComponent from './ETTSelectComponent';
import ETTSkillExtractionComponent from './ETTSkillExtractionComponent';
import ETTSuggestionComponent from './ETTSuggestionComponent';
import ETTSuperPowersComponent from './ETTSuperPowersComponent';
import ETTTextComponent from './ETTTextComponent';
import ETTTextInputComponent from './ETTTextInputComponent';
import ETTWelcomeComponent from './ETTWelcomeComponent';

export const COMPONENT_TYPE_LABELS: Record<TEmployeeTalkComponentType, string> = {
    text: 'Text Display',
    textInput: 'Text Input',
    multilineInput: 'Multiline Input',
    select: 'Dropdown Select',
    checkbox: 'Checkboxes',
    suggestion: 'Suggestion Form',
    nameGender: 'Personal Info',
    cvUpload: 'CV Upload',
    skillExtraction: 'Skill Extraction',
    onboardingProgress: 'Onboarding Progress',
    aiAssistant: 'AI Assistant',
    welcome: 'Welcome Page',
    superPowers: 'SuperPowers Selection',
    certificateSelection: 'Certificate Selection',
    roleSelection: 'Role Selection',
    resultsOverview: 'Results Overview',
    completion: 'Completion',
};

export const getComponentByType = (
    type: TEmployeeTalkComponentType,
    props: ITemplateComponentProps,
): React.ReactNode => {
    switch (type) {
        case 'text':
            return <ETTTextComponent {...props} />;
        case 'textInput':
            return <ETTTextInputComponent {...props} />;
        case 'multilineInput':
            return <ETTMultilineInputComponent {...props} />;
        case 'select':
            return <ETTSelectComponent {...props} />;
        case 'checkbox':
            return <ETTCheckboxComponent {...props} />;
        case 'suggestion':
            return <ETTSuggestionComponent {...props} />;
        case 'nameGender':
            return <ETTNameGenderComponent {...props} />;
        case 'cvUpload':
            return <ETTOnboardingCVUploadComponent {...props} />;
        case 'skillExtraction':
            return <ETTSkillExtractionComponent {...props} />;
        case 'onboardingProgress':
            return <ETTOnboardingProgressComponent {...props} />;
        case 'aiAssistant':
            return <ETTAIAssistantComponent {...props} />;
        case 'welcome':
            return <ETTWelcomeComponent {...props} />;
        case 'superPowers':
            return <ETTSuperPowersComponent {...props} />;
        case 'certificateSelection':
            return <ETTCertificateSelectionComponent {...props} />;
        case 'roleSelection':
            return <ETTRoleSelectionComponent {...props} />;
        case 'resultsOverview':
            return <ETTResultsOverviewComponent {...props} />;
        case 'completion':
            return <ETTCompletionComponent {...props} />;
        default:
            return <div>Unknown component type: {type}</div>;
    }
};

export const getDefaultComponentProperties = (
    type: TEmployeeTalkComponentType,
    lang: ELanguage,
): TEmployeeTalkComponentProperties => {
    const langStrings = getLanguageTexts(lang).employeeTalk;

    switch (type) {
        case 'text':
            return { text: langStrings.text } as ITextComponentProperties;
        case 'textInput':
            return {
                placeholder: langStrings.placeholder,
                maxLength: 255,
                defaultValue: '',
            } as ITextInputComponentProperties;
        case 'multilineInput':
            return {
                placeholder: langStrings.placeholder,
                rows: 4,
                maxLength: 1000,
                defaultValue: '',
            } as IMultilineInputComponentProperties;
        case 'select':
            return {
                options: [langStrings.option1, langStrings.option2, langStrings.option3],
                multiple: false,
            } as ISelectComponentProperties;
        case 'checkbox':
            return {
                options: [langStrings.option1, langStrings.option2, langStrings.option3],
            } as ICheckboxComponentProperties;
        case 'suggestion':
            return {
                enabledEntities: {
                    skills: true,
                    certificates: true,
                    roles: true,
                },
                requireJustification: false,
                formState: {
                    skills: [],
                    certificates: [],
                    roles: [],
                    justification: '',
                },
            } as ISuggestionComponentProperties;
        case 'nameGender':
            return {
                showUpdateButton: true,
            } as INameGenderComponentProperties;
        case 'cvUpload':
            return {
                maxFileSize: 10,
                allowedFormats: ['pdf', 'doc', 'docx'],
                autoAnalyze: true,
            } as ICVUploadComponentProperties;
        case 'skillExtraction':
            return {
                showConfidenceScore: true,
                allowManualSkills: true,
                requireLevelRating: true,
                hideLabels: true,
            } as ISkillExtractionComponentProperties;
        case 'onboardingProgress':
            return {
                showPercentage: true,
                showStepNames: true,
            } as IOnboardingProgressComponentProperties;
        case 'aiAssistant':
            return {
                assistantName: 'Onboarding Assistant',
                welcomeMessage: 'Hallo! Ich bin hier, um Ihnen beim Onboarding zu helfen.',
                enableChat: true,
                enableFormHelp: true,
                position: 'fixed',
            } as IAIAssistantComponentProperties;
        case 'welcome':
            return {
                showProcessOverview: true,
                customWelcomeText: '',
            } as IWelcomeComponentProperties;
        case 'superPowers':
            return {
                maxSelection: 4,
            } as import('@sharedInterfaces/IEmployeeTalkTemplate').ISuperPowersComponentProperties;
        case 'certificateSelection':
            return {
                showConfidence: true,
            } as import('@sharedInterfaces/IEmployeeTalkTemplate').ICertificateSelectionComponentProperties;
        case 'roleSelection':
            return {
                showConfidence: false,
            } as import('@sharedInterfaces/IEmployeeTalkTemplate').IRoleSelectionComponentProperties;
        case 'resultsOverview':
            return {
                showStatistics: true,
            } as import('@sharedInterfaces/IEmployeeTalkTemplate').IResultsOverviewComponentProperties;
        case 'completion':
            return {
                showNextSteps: true,
            } as import('@sharedInterfaces/IEmployeeTalkTemplate').ICompletionComponentProperties;
        default:
            throw new Error(`Unknown component type: ${type}`);
    }
};

export const createNewComponent = (
    type: TEmployeeTalkComponentType,
    order: number,
    lang: ELanguage,
): IEmployeeTalkTemplateComponent => {
    const langStrings = getLanguageTexts(lang).employeeTalk;

    let label = `${langStrings.new || 'New'} ${type.charAt(0).toUpperCase() + type.slice(1)}`;
    let description = '';
    let system = false;

    // Spezielle Behandlung für Suggestion-Komponenten
    if (type === 'suggestion') {
        label = langStrings.suggestionDefaultTitle;
        description = langStrings.suggestionDefaultDescription;
    }

    // Spezielle Behandlung für NameGender-Komponenten
    if (type === 'nameGender') {
        label = langStrings.nameGenderDefaultTitle;
        description = langStrings.nameGenderDefaultDescription;
        system = true; // NameGender-Komponenten sind standardmäßig System-Komponenten
    }

    // Spezielle Behandlung für Onboarding-Komponenten
    if (type === 'cvUpload') {
        label = 'CV Upload';
        description = 'Lebenslauf hochladen und analysieren lassen (Onboarding-Design)';
        system = true;
    }
    if (type === 'skillExtraction') {
        label = 'Skill Extraktion';
        description = 'Skills aus CV-Analyse auswählen und bewerten';
        system = true;
    }
    if (type === 'onboardingProgress') {
        label = 'Onboarding Fortschritt';
        description = 'Fortschrittsanzeige für das Onboarding';
        system = true;
    }
    if (type === 'aiAssistant') {
        label = 'KI-Assistent';
        description = 'Intelligenter Assistent für Hilfe und Unterstützung';
        system = true;
    }
    if (type === 'welcome') {
        label = 'Willkommen';
        description = 'Personalisierte Willkommensnachricht mit Onboarding-Übersicht';
        system = true;
    }
    if (type === 'superPowers') {
        label = 'SuperPowers';
        description = 'Top Skills Auswahl mit Celebration-Effekten';
        system = true;
    }
    if (type === 'certificateSelection') {
        label = 'Zertifikate Auswahl';
        description = 'Zertifikate aus CV-Analyse bestätigen';
        system = true;
    }
    if (type === 'roleSelection') {
        label = 'Rollen Auswahl';
        description = 'Passende Rollen aus CV-Analyse auswählen';
        system = true;
    }
    if (type === 'resultsOverview') {
        label = 'Ergebnis-Übersicht';
        description = 'Zusammenfassung aller ausgewählten Items';
        system = true;
    }
    if (type === 'completion') {
        label = 'Fertigstellung';
        description = 'Abschluss-Seite mit Celebration und nächsten Schritten';
        system = true;
    }

    return {
        id: `component-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        type,
        label,
        description,
        required: false,
        order,
        properties: getDefaultComponentProperties(type, lang),
        system,
    };
};
